import React, { useState, useEffect } from "react";
import { Card, Col, Container, Row } from "reactstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import _ from "lodash";

import HeaderCreateItem from "components/Common/HeaderCreateItem";
import { CommonButton } from "components/Common/ButtonCommon";
import useGetFormSchema from "custom-hook/useGetFormSchema";
import httpService from "services/httpService";
import { addUser } from "redux-toolkit/slices/Users/userSlice";
import {
  addEmployee,
  updateEmployee,
} from "redux-toolkit/slices/Employee/EmployeeSlice";
import ViewableCommon from "components/Common/ViewableCommon";
import { CommonText } from "components/Common/TextCommon";
import { CommonInputt, CommonLabel } from "components/Common/inputCommon";
import { toastrErrorAlert } from "components/Common/AlertToastr";
import InputField from "components/form-control/InputField";
import VVSSelectRelation from "components/Common/VVSSelectRelation";
import moment from "moment";
import useFormWithSaveChecked from "custom-hook/useFormWithSaveChecked";

const NewEmployee = () => {
  const [userDetail, setUserDetail] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const excludeFields = id ? [] : ["createdAt", "updatedAt"];

  useEffect(() => {
    getEmployee();
    getEmployeeRole();
  }, [id]);

  const schema = yup
    .object({
      fullName: yup.string().required("Vui lòng nhập Họ tên!"),
      phone: yup.string().required("Vui lòng nhập Số điện thoại!"),
      email: yup.string().email("Email không đúng định dạng!"),
      password: yup.string(),
      birthday: yup
        .object()
        .shape({ __type: yup.string(), iso: yup.string() })
        .nullable()
        .transform((_, val) => (val ? { __type: "Date", iso: val } : null)),
      branch: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val.text
            ? { __type: "Pointer", className: "Branch", objectId: val.objectId }
            : null
        ),
      department: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "Department",
                objectId: val.objectId,
              }
            : null
        ),
      group: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "EmployeeGroup",
                objectId: val.objectId,
              }
            : null
        ),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors },
    saved,
    ...rest
  } = useFormWithSaveChecked({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { renderForm, getAddress } = useGetFormSchema(
    "Employee",
    yup,
    rest,
    errors
  );

  const getEmployee = async () => {
    if (!id) return;

    const res = await httpService.get(
      `/parse/classes/Employee/${id}?include=["branch","department","user","group","grade"]`
    );

    const { reset } = rest;
    reset({
      ...res,
      birthday: moment(res?.birthday?.iso || "").format("YYYY-MM-DD"),
      branch: res?.branch
        ? {
            text: res.branch.name,
            objectId: res.branch.objectId,
            className: "Branch",
            __type: "Pointer",
          }
        : { text: "" },
      group: res?.group
        ? {
            text: res.group.name,
            objectId: res.group.objectId,
            className: "EmployeeGroup",
            __type: "Pointer",
          }
        : { text: "" },
      department: res?.department
        ? {
            text: res.department.name,
            objectId: res.department.objectId,
            className: "Department",
            __type: "Pointer",
          }
        : { text: "" },
      grade: res?.grade
        ? {
            text: res.grade.name,
            objectId: res.grade.objectId,
            className: "EmployeeGrade",
            __type: "Pointer",
          }
        : { text: "" },
      createdAt: moment(res.createdAt).format("YYYY-MM-DD"),
      updatedAt: moment(res.updatedAt).format("YYYY-MM-DD"),
    });
    getAddress(res);
    setUserDetail(res?.user);
  };

  const getEmployeeRole = async () => {
    const res = await httpService.get('/parse/roles?where={"name": "Staff"}');
    const staffRoleRes = res.results[0];
    const staffRole = {
      __type: "Pointer",
      className: "_Role",
      objectId: staffRoleRes.objectId,
    };
    setUserRole(staffRole);
  };

  const onSubmit = async values => {
    const {
      createdAt,
      updatedAt,
      username,
      password,
      user,
      grade,
      ...employeeValues
    } = values;

    const employeeCopie = {
      ...employeeValues,
      ...(grade.text !== "" && {
        grade: {
          __type: "Pointer",
          className: "EmployeeGrade",
          objectId: grade.objectId,
        },
      }),
    };

    const employee = { ...employeeCopie };

    const userInfo = {
      status: employee.isActive ? "active" : "inactive",
      fullName: employee.fullName,
      ...(employee.email && { email: employee.email }),
      phone: employee.phone,
      username,
      password,
    };

    if (id) {
      dispatch(updateEmployee({ dataItem: employee, dataId: id }));
      return;
    }

    if (username && password) {
      try {
        userInfo.dob = userInfo.dob
          ? { __type: "Date", iso: userInfo.dob }
          : null;
        const userResponse = await httpService.post("/parse/users", userInfo);

        const userObjectId = userResponse.objectId;
        await httpService.put(`/parse/roles/${userRole.objectId}`, {
          users: {
            __op: "AddRelation",
            objects: [
              { __type: "Pointer", className: "_User", objectId: userObjectId },
            ],
          },
        });
        const employeeWithUser = {
          ...employee,
          user: {
            __type: "Pointer",
            className: "_User",
            objectId: userObjectId,
          },
        };
        const employeeObjectId = (await dispatch(addEmployee(employeeWithUser)))
          .payload.objectId;
        history.replace(`/employee/${employeeObjectId}`);
        return;
      } catch (errorRes) {
        if (errorRes?.response?.data?.error) {
          toastrErrorAlert(errorRes.response?.data.error);
        }
        return;
      }
    }

    const EOI = (await dispatch(addEmployee(employee))).payload.objectId;
    history.replace(`/employee/${EOI}`);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <HeaderCreateItem title="Thêm / Chi Tiết Nhân Viên" saved={saved}>
            <CommonButton level={0}>Lưu</CommonButton>
          </HeaderCreateItem>
          {renderForm(excludeFields)}
          <ViewableCommon
            if={() => !id}
            caseTrue={
              <Card body>
                <CommonText className="m-0" level={1}>
                  Thông tin tài khoản
                </CommonText>
                <Row>
                  <Col>
                    <InputField
                      label="Tên đăng nhập"
                      name="username"
                      errors={errors}
                      {...rest}
                    />
                  </Col>
                  <Col>
                    <InputField
                      label="Mật khẩu"
                      name="password"
                      errors={errors}
                      {...rest}
                    />
                  </Col>
                  <Col>
                    <VVSSelectRelation
                      label="Nhóm quyền"
                      name="roles"
                      model="_Role"
                      errors={errors}
                      {...rest}
                    />
                  </Col>
                </Row>
              </Card>
            }
          />
          <ViewableCommon
            if={() => id && userDetail}
            caseTrue={
              <Card body>
                <CommonText level={1} className="m-0">
                  <span>Thông tin tài khoản </span>
                  <Link
                    className="link-detail"
                    to={`/account/${userDetail?.objectId}`}
                  >
                    (chi tiết)
                  </Link>
                </CommonText>
                <Row>
                  <Col>
                    <CommonLabel>Username</CommonLabel>
                    <CommonInputt
                      className="form-control"
                      value={userDetail?.username || ""}
                      readOnly
                      onChange={() => {}}
                    />
                  </Col>
                  <Col>
                    <CommonLabel>Họ tên</CommonLabel>
                    <CommonInputt
                      className="form-control"
                      value={userDetail?.fullName || ""}
                      readOnly
                      onChange={() => {}}
                    />
                  </Col>
                  <Col>
                    <CommonLabel>Email</CommonLabel>
                    <CommonInputt
                      className="form-control"
                      value={userDetail?.email || ""}
                      readOnly
                      onChange={() => {}}
                    />
                  </Col>
                </Row>
              </Card>
            }
          />
        </form>
      </Container>
    </div>
  );
};

export default NewEmployee;
